import React, { useEffect, useState } from "react";
import { Box, Typography, Container, Paper } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import bkstApi from "../../common/component/api";
import { useParams } from "react-router-dom";

export default function Prospects() {
  const { bakerId } = useParams();
  const [config, setConfig] = useState(null);
  const [sizeChartArray, setSizeChartArray] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    
    // Fetch baker config
    bkstApi.get(`/baker/${bakerId}/config`).then((res) => {
      setConfig(res.data.config);
      
      // Mock size chart array - replace with actual data source
      const mockSizeChart = [
        { id: 1, name: "Small", size: "8-inch Round", servings: "6-8" },
        { id: 2, name: "Medium", size: "10-inch Round", servings: "10-12" },
        { id: 3, name: "Large", size: "12-inch Round", servings: "16-20" },
        { id: 4, name: "X-Large", size: "14-inch Round", servings: "24-30" }
      ];
      setSizeChartArray(mockSizeChart);
      setLoading(false);
    });
  }, [bakerId]);

  // Create columns for DataGrid
  const createColumns = () => {
    const columns = [
      {
        field: 'name',
        headerName: 'Size',
        width: 150,
        flex: 1
      }
    ];

    // Add flavor columns from catalog
    if (config?.catalog?.["cake"]?.variants?.flavor?.field?.options) {
      const flavors = config.catalog["cake"].variants.flavor.field.options;
      flavors.forEach((flavor, index) => {
        columns.push({
          field: `flavor_${index}`,
          headerName: flavor.name,
          width: 120,
          flex: 1,
          renderCell: (params) => {
            // Check if this flavor is available for this size
            const hasFlavor = checkFlavorAvailability(params.row, flavor);
            return hasFlavor ? '✅' : '❌';
          }
        });
      });
    }

    return columns;
  };

  // Check if a flavor is available for a specific size
  const checkFlavorAvailability = (sizeRow, flavor) => {
    // Check if flavor has override pricing for this size
    if (flavor.override && flavor.override.size) {
      const sizeKey = `${sizeRow.name || sizeRow.size}`;
      return flavor.override.size[sizeKey] !== undefined;
    }
    
    // Check if flavor has restrictions for this size
    if (flavor.override && flavor.override.size) {
      const sizeKey = `${sizeRow.name || sizeRow.size}`;
      const override = flavor.override.size[sizeKey];
      if (override && override.restrict === true) {
        return false;
      }
    }
    
    // Default availability based on pricing type
    return flavor.pricing && (flavor.pricing.type === 'fixed' || flavor.pricing.type === 'variable');
  };

  // Create rows for DataGrid
  const createRows = () => {
    return sizeChartArray.map((size) => ({
      id: size.id,
      name: size.name || size.size,
      ...size
    }));
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box my={4}>
          <Typography>Loading...</Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box my={4}>
        <Paper elevation={2} sx={{ p: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Size Chart & Flavor Availability
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Showing size chart items with flavor availability matrix.
          </Typography>
          
          <Box sx={{ height: 400, width: '100%' }}>
            <DataGrid
              rows={createRows()}
              columns={createColumns()}
              pageSize={10}
              rowsPerPageOptions={[5, 10, 25]}
              disableSelectionOnClick
              sx={{
                backgroundColor: "#fff",
                '& .MuiDataGrid-cell': {
                  borderBottom: '1px solid #e0e0e0',
                },
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: '#f5f5f5',
                },
              }}
            />
          </Box>
        </Paper>
      </Box>
    </Container>
  );
}
